import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import StyledText from "../../components/StyledComponents/StyledText";
import MultiSelectDropdown from "../../components/form/MultiSelectDropdown";
import { Button } from "../../components/form/Button";
import CrossIcon from "../../assets/icons/CrossIcon";
import SaveIcon from "../../assets/icons/SaveIcon";
import { Field, Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import FormikSelect from "../../components/Formik/FormikSelect";
import { useGetFieldsDataMutation } from "../../api/accountApiSlice";
import { useToast } from "../../components/toast/ToastContext";
import { useBulkUpdateFieldsMutation } from "../../api/searchQueryApiSlice";
import { useSelector } from "react-redux";
import { selectAuth, selectIsLoggedIn } from "../auth/authSlice";

interface DirectorySettingsValues {
  directoryPrivacy: string | "";
  infoToInclude: string[];
}

const directoryPrivacyOptions = [
  { value: "public", label: "Public (Everyone)" },
  { value: "private", label: "Private" },
];

const validationSchema = Yup.object({
  directoryPrivacy: Yup.string().required("Directory privacy is required"),
  // infoToInclude: Yup.array().min(1, "Select at least one information type"),
});

const FormikMultiSelect = ({
  field,
  form,
  options,
  isLoading,
  ...props
}: any) => {
  return (
    <MultiSelectDropdown
      options={options}
      selected={field.value}
      onSelect={(val: string) => {
        const currentValues = form.values[field.name] || [];
        const newValues = currentValues.includes(val)
          ? currentValues.filter((item: string) => item !== val)
          : [...currentValues, val];

        form.setFieldValue(field.name, newValues, true);
        form.setFieldTouched(field.name, true, true);
      }}
      count
      isLoading={isLoading}
      {...props}
    />
  );
};

const buildPayload = (
  values: DirectorySettingsValues,
  directoryFields: any[],
  recordIds: number[],
) => {
  const baseFields = directoryFields
  .filter(f => f.value !== "contact_show_in_directory") // Remove if already present
  .map(field => ({
    fieldAccessKey: field.value,
    value: values.infoToInclude.includes(field.value) ? "true" : "false",
  }));

  const fields = [
    ...baseFields,
    {
      fieldAccessKey: "contact_show_in_directory",
      value: values.directoryPrivacy === "public" ? "true" : "false",
    }
  ];

  return {
    fields,
    scope: "contact",
    recordIds,
  };
};

const MemberDirectorySettings = () => {
  const [apiError, setApiError] = useState("");
  const [apiSuccess, setApiSuccess] = useState("");
  const showToast = useToast();

  const [directoryFields, setDirectoryFields] = useState<any>(null);
  const [selectedInfo, setSelectedInfo] = useState<string[]>([]);

  const [getFields, { isLoading: loadingFields }] = useGetFieldsDataMutation();

  const [updateFields, { isLoading: isUpdating }] =
    useBulkUpdateFieldsMutation();

  const auth = useSelector(selectAuth);

  const payload = useMemo(
    () => ({
      scope: "contact",
      recordIds: [auth.userId],
      fieldsAccessKeys: [
        "contact_home_phone_directory",
        "contact_fax_directory",
        "contact_tollfree_phone_directory",
        "contact_other_phone_directory",
        "contact_mobile_phone_directory",
        "contact_work_phone_directory",
        "contact_other_email_directory",
        "contact_work_email_directory",
        "contact_other_address_directory",
        "contact_work_address_directory",
        "contact_show_in_directory",
        "contact_personal_email_directory",
        "contact_home_address_directory",
      ],
    }),
    [],
  );

  const fetchFields = async () => {
      try {
        const allFields = await getFields(payload).unwrap();

        // const mappedFields = allFields?.data[0].fields.map((field: any) => ({
        //   name: field?.fieldName || "",
        //   value: field?.accessKey || "",
        // }));
        const mappedFields = allFields?.data?.[0]?.fields
        ?.filter((field: any) => field?.accessKey !== 'contact_show_in_directory')
        .map((field: any) => ({
          name: field?.fieldName || "",
          value: field?.accessKey || "",
        })) || [];

        const selectedFields = allFields?.data[0].fields
          .filter(
            (field: any) => (field.value === "true" || field.value === true)
          )
          .map((field: any) => field.accessKey);

        setDirectoryFields(mappedFields);
        setSelectedInfo(selectedFields);

        showToast("Success", "Fields retrieved successfully", "success");
      } catch (err: any) {
        const errorMessage =
          err?.data?.message || err?.error || "Failed to retrieve fields";

        showToast("Error", errorMessage, "error");
      }
    };

  useEffect(() => {

    fetchFields();
  }, [getFields, payload]);


  // 🔹 Initial form values
  const initialValues = useMemo<DirectorySettingsValues>(
    () => ({
      directoryPrivacy: selectedInfo.length === 0
        ? ""
        : selectedInfo.includes("contact_show_in_directory")
        ? "public"
        : "private",
      infoToInclude: selectedInfo || [],
    }),
    [selectedInfo],
  );

  // 🔹 Form submit handler
  const handleSubmit = async (
    values: DirectorySettingsValues,
    { setSubmitting, resetForm }: FormikHelpers<DirectorySettingsValues>,
  ) => {
    
    setApiError("");
    setApiSuccess("");

    try {
      if (!auth || !auth.userId) {
        showToast("Error", "User not authenticated", "error");
        return;
      }
      const payload = buildPayload(values, directoryFields || [], [auth.userId]);

      await updateFields(payload).unwrap();

      showToast(
        "Success",
        "Member Directory Updated successfully.",
        "success",
      );

      await fetchFields();

    } catch (err: any) {
      const errorMessage =
        err?.data?.message ||
        err?.error ||
        "Failed to update directory settings";
      showToast("Error", errorMessage, "error");
    }
  };

  return (
    <CardContainer>
      <div className="border-bottom mb-3 pb-3">
        <StyledText
          fontSize="15px"
          fontWeight={700}
          textColor={"var(--app-primary-color)"}
        >
          Member Directory Settings
        </StyledText>
      </div>
      <StyledText
        fontSize="15px"
        fontWeight={400}
        lineHeight="27px"
        textColor={"var(--app-text-color)"}
        style={{ marginBottom: "30px" }}
      >
        Manage and customize how your profile information is displayed to other
        members within your organization’s directory.
      </StyledText>
      <Formik
        // key={selectedInfo.join(",")}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          errors,
          touched,
          isSubmitting,
          handleChange,
          handleReset,
          dirty,
          isValid,
          values,
        }) => (
          <Form>
            {apiError && (
              <StyledText
                fontSize="13px"
                textColor="var(--app-error-color, #dc3545)"
                className="mb-3"
                style={{ display: "block" }}
              >
                {apiError}
              </StyledText>
            )}
            {apiSuccess && (
              <StyledText
                fontSize="13px"
                textColor="var(--app-success-color, #28a745)"
                className="mb-3"
                style={{ display: "block" }}
              >
                {apiSuccess}
              </StyledText>
            )}
            <StyledText
              fontSize="14px"
              fontWeight={600}
              textColor={"var(--app-secondary-color)"}
              style={{ marginBottom: "10px" }}
            >
              Directory Privacy
            </StyledText>
            {/* <Field
              name="directoryPrivacy"
              component={FormikAMSSelect}
              options={directoryPrivacyOptions}
              placeholder="Select directory privacy"
              isSearchable={false}
              isClearable={false}
            />
            {touched.directoryPrivacy && errors.directoryPrivacy && (
              <StyledText
                fontSize='12px'
                textColor='var(--app-error-color, #dc3545)'
                className='mt-1'
                style={{ display: 'block' }}
              >
                {errors.directoryPrivacy}
              </StyledText>
            )} */}
            <FormikSelect
              name="directoryPrivacy"
              placeholder="Select directory privacy"
              options={directoryPrivacyOptions}
            />
            <StyledText
              fontSize="14px"
              fontWeight={600}
              textColor={"var(--app-secondary-color)"}
              style={{ marginBottom: "10px", marginTop: "30px" }}
            >
              Information To Include
            </StyledText>

            <Field
              name="infoToInclude"
              component={FormikMultiSelect}
              options={directoryFields || []}
              placeholder="Select information to include"
              isLoading={loadingFields}
            />
            {touched.infoToInclude && errors.infoToInclude && (
              <StyledText
                fontSize="12px"
                textColor="var(--app-error-color, #dc3545)"
                className="mt-1"
                style={{ display: "block" }}
              >
                {errors.infoToInclude}
              </StyledText>
            )}
            {/* <FormikSelect 
                name='infoToInclude'
                placeholder='Select information to include'
                options={infoToIncludeOptions}
            /> */}
            {values.infoToInclude.length > 0 && (
              <div
                className="d-flex"
                style={{
                  marginTop: "30px",
                  marginBottom: "20px",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                {values.infoToInclude.map((infoValue) => {
                  const infoItem = directoryFields?.find(
                    (item: any) => item.value === infoValue,
                  );
                  return infoItem ? (
                    <TagStyles key={infoItem.value}>{infoItem.name}</TagStyles>
                  ) : null;
                })}
              </div>
            )}

            {dirty && (
              <div className="d-flex justify-content-end gap-2 mt-4 pb-2">
                <Button
                  btnBg="#ffffff"
                  color="rgba(88, 85, 88, 1)"
                  borderColor="rgba(186, 189, 193, 1)"
                  $variant="customButton"
                  onClick={() => {
                    handleReset();
                    setApiError("");
                    setApiSuccess("");
                  }}
                >
                  <CrossIcon size={20} /> Close
                </Button>
                <Button
                  btnBg={"var(--app-primary-color)"}
                  color={"var(--app-text-color)"}
                  // borderColor='rgba(186, 189, 193, 1)'
                  $variant="customButton"
                  disabled={!isValid || isSubmitting}
                >
                  {" "}
                  {isUpdating ? (
                    <span>Updating...</span>
                  ) : (
                    <>
                      <SaveIcon size={20} color="var(--app-text-color)" />{" "}
                      Update Directory
                    </>
                  )}
                </Button>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </CardContainer>
  );
};

export default MemberDirectorySettings;

export const CardContainer = styled.div`
  background: rgba(255, 255, 255, 1);
  border-radius: 10px;
  padding: 20px 25px 25px 25px;
`;

const TagStyles = styled.div`
  border-radius: 28px;
  background-color: #f5f5f5;
  padding: 8px 12px;
  border: 0.5px solid rgba(235, 234, 235, 1);
  font-size: 12px;
  font-weight: 400;
`;
